<script setup>
import { computed, defineProps, defineModel, ref } from 'vue';
import WithLabel from './WithLabel.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  icon: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  hasError: Boolean,
  errorMessage: {
    type: String,
    default: '',
  },
  spacing: {
    type: String,
    default: 'base',
    validator: value => ['base', 'compact'].includes(value),
  },
});

defineOptions({
  inheritAttrs: false,
});

const model = defineModel({
  type: [String, Number],
  required: true,
});

const isPasswordVisible = ref(false);

const currentInputType = computed(() => {
  if (props.type !== 'password') {
    return props.type;
  }

  return isPasswordVisible.value ? 'text' : 'password';
});

const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value;
};
</script>

<template>
  <WithLabel
    :label="label"
    :icon="icon"
    :name="name"
    :has-error="hasError"
    :error-message="errorMessage"
  >
    <template #rightOfLabel>
      <slot />
    </template>
    <input
      v-bind="$attrs"
      v-model="model"
      :type="currentInputType"
      class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 appearance-none outline outline-1 focus:outline focus:outline-1 text-n-slate-12 placeholder:text-n-slate-10 sm:text-sm sm:leading-6 px-3 py-3"
      :class="{
        'error outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8':
          hasError,
        'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
          !hasError,
        'px-3 py-3': spacing === 'base',
        'px-3 py-2 mb-0': spacing === 'compact',
        'pl-9': icon,
        'pr-10': type === 'password',
      }"
    />
    <button
      v-if="type === 'password'"
      type="button"
      class="absolute inset-y-0 right-3 inline-flex items-center text-n-slate-10 hover:text-n-slate-12 transition-colors"
      :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
      @click="togglePasswordVisibility"
    >
      <FluentIcon
        size="18"
        :icon="isPasswordVisible ? 'eye-hide-outline' : 'eye-show-outline'"
      />
    </button>
  </WithLabel>
</template>
